import { getCSRFHeaders } from '../utils/csrf';
import { UserRegistrationData } from '../schemas/auth';
import { useAuthStore } from '../stores/authStore';

// API Base URL from environment
const API_BASE_URL =
  import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000';

// API Response Types
export interface RegistrationResponse {
  message: string;
  user_id: string;
  email: string;
  verification_required: boolean;
}

export interface LoginResponse {
  access_token: string;
  refresh_token: string;
  token_type: string;
  expires_in: number;
  user: {
    id: string;
    email: string;
    first_name: string;
    last_name: string;
    role: string;
    is_verified: boolean;
    last_login_at?: string;
  };
  security_info: {
    device_id: string;
    is_new_device: boolean;
    requires_verification: boolean;
    trust_score: number;
    geographic_anomaly: boolean;
    last_login_location?: string;
  };
}

export interface LoginRequest {
  email: string;
  password: string;
  device_info?: any;
  location?: {
    latitude: number;
    longitude: number;
    accuracy: number;
  };
  remember_device?: boolean;
}

export interface RefreshTokenResponse {
  access_token: string;
  token_type: string;
  expires_in: number;
}

export interface EmailVerificationResponse {
  message: string;
  email_verified: boolean;
  account_activated: boolean;
}

export interface PasswordResetResponse {
  message: string;
  email: string;
  reset_token_sent: boolean;
}

export interface PasswordResetConfirmResponse {
  message: string;
  password_reset: boolean;
  login_required: boolean;
}

export interface ChangePasswordRequest {
  current_password: string;
  new_password: string;
  confirm_password: string;
}

export interface ChangePasswordResponse {
  message: string;
  password_changed: boolean;
  logout_required: boolean;
}

export interface VerifyResetTokenResponse {
  token_valid: boolean;
  email?: string;
  expires_at?: string;
}

export interface ApiError {
  detail: string | { 
    error?: string; 
    message: string; 
    retry_after?: number;
    lockout_duration?: number;
    account_locked?: boolean;
  };
}

export interface AuthError extends Error {
  status: number;
  code?: string;
  details?: {
    lockout_duration?: number;
    account_locked?: boolean;
    retry_after?: number;
  };
}

// API Client Class
class AuthApiClient {
  private baseUrl: string;

  constructor(baseUrl: string = API_BASE_URL) {
    this.baseUrl = baseUrl;
  }

  private async makeRequest<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    const url = `${this.baseUrl}/api/v1${endpoint}`;

    // Get auth token from Zustand store (single source of truth)
    const token = useAuthStore.getState().getAccessToken();

    // Add CSRF headers for state-changing operations
    const csrfHeaders = ['POST', 'PUT', 'PATCH', 'DELETE'].includes(
      options.method || 'GET'
    )
      ? getCSRFHeaders()
      : {};

    // Build headers with token if available
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...csrfHeaders,
      ...(options.headers || {}),
    };

    // Add Authorization header if token exists
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const response = await fetch(url, {
      ...options,
      headers,
    });

    const data = await response.json();

    if (!response.ok) {
      const apiError: ApiError = data;
      const authError = new Error() as AuthError;
      
      // Set error message
      if (typeof apiError.detail === 'string') {
        authError.message = apiError.detail;
      } else {
        authError.message = apiError.detail?.message || 'An error occurred';
      }
      
      // Set status code
      authError.status = response.status;
      
      // Set error code based on HTTP status
      if (response.status === 423) {
        authError.code = 'ACCOUNT_LOCKED';
      } else if (response.status === 401) {
        authError.code = 'INVALID_CREDENTIALS';
      }
      
      // Set additional details for structured error data
      if (typeof apiError.detail === 'object' && apiError.detail) {
        authError.details = {
          lockout_duration: apiError.detail.lockout_duration,
          account_locked: apiError.detail.account_locked,
          retry_after: apiError.detail.retry_after,
        };
      }
      
      throw authError;
    }

    return data;
  }

  async login(loginData: LoginRequest): Promise<LoginResponse> {
    return this.makeRequest<LoginResponse>('/auth/login', {
      method: 'POST',
      body: JSON.stringify(loginData),
    });
  }

  async refreshToken(refreshToken: string): Promise<RefreshTokenResponse> {
    return this.makeRequest<RefreshTokenResponse>('/auth/refresh-token', {
      method: 'POST',
      body: JSON.stringify({ refresh_token: refreshToken }),
    });
  }

  async logout(refreshToken: string): Promise<{ message: string }> {
    return this.makeRequest<{ message: string }>('/auth/logout', {
      method: 'POST',
      body: JSON.stringify({ refresh_token: refreshToken }),
    });
  }

  async register(
    userData: UserRegistrationData
  ): Promise<RegistrationResponse> {
    return this.makeRequest<RegistrationResponse>('/auth/register', {
      method: 'POST',
      body: JSON.stringify(userData),
    });
  }

  async verifyEmail(token: string): Promise<EmailVerificationResponse> {
    return this.makeRequest<EmailVerificationResponse>('/auth/verify-email', {
      method: 'POST',
      body: JSON.stringify({ token }),
    });
  }

  async resendVerification(
    email: string
  ): Promise<{ message: string; email: string }> {
    return this.makeRequest<{ message: string; email: string }>(
      '/auth/resend-verification',
      {
        method: 'POST',
        body: JSON.stringify({ email }),
      }
    );
  }

  async requestPasswordReset(email: string): Promise<PasswordResetResponse> {
    return this.makeRequest<PasswordResetResponse>('/auth/forgot-password', {
      method: 'POST',
      body: JSON.stringify({ email }),
    });
  }

  async verifyResetToken(token: string): Promise<VerifyResetTokenResponse> {
    return this.makeRequest<VerifyResetTokenResponse>(
      `/auth/verify-reset-token?token=${encodeURIComponent(token)}`
    );
  }

  async resetPassword(
    token: string,
    newPassword: string,
    confirmPassword: string
  ): Promise<PasswordResetConfirmResponse> {
    return this.makeRequest<PasswordResetConfirmResponse>(
      '/auth/reset-password',
      {
        method: 'POST',
        body: JSON.stringify({
          token,
          new_password: newPassword,
          confirm_password: confirmPassword,
        }),
      }
    );
  }

  async getProfile(): Promise<{
    id: string;
    email: string;
    first_name: string;
    last_name: string;
    role: string;
    subscription_tier: string;
    is_active: boolean;
    email_verified: boolean;
    created_at: string;
    last_login_at?: string;
  }> {
    // Token will be added by makeRequest from Authorization header
    return this.makeRequest('/auth/profile', {
      method: 'GET',
    });
  }

  async updateProfile(updateData: {
    subscription_tier?: string;
    first_name?: string;
    last_name?: string;
    role?: string;
    phone_number?: string;
  }): Promise<{
    id: string;
    email: string;
    first_name: string;
    last_name: string;
    role: string;
    subscription_tier: string;
    is_active: boolean;
    email_verified: boolean;
    created_at: string;
    last_login_at?: string;
  }> {
    // Token and CSRF headers will be added by makeRequest
    return this.makeRequest('/auth/profile', {
      method: 'PUT',
      body: JSON.stringify(updateData),
    });
  }

  async changePassword(
    passwordData: ChangePasswordRequest
  ): Promise<ChangePasswordResponse> {
    // Token and CSRF headers will be added by makeRequest
    return this.makeRequest<ChangePasswordResponse>('/auth/change-password', {
      method: 'POST',
      body: JSON.stringify(passwordData),
    });
  }

  // Password strength checker utility
  static checkPasswordStrength(password: string): {
    score: number;
    feedback: string[];
    isValid: boolean;
  } {
    const feedback: string[] = [];
    let score = 0;

    if (password.length >= 8) score += 1;
    else feedback.push('At least 8 characters');

    if (/[A-Z]/.test(password)) score += 1;
    else feedback.push('One uppercase letter');

    if (/[a-z]/.test(password)) score += 1;
    else feedback.push('One lowercase letter');

    if (/\d/.test(password)) score += 1;
    else feedback.push('One number');

    if (/[!@#$%^&*(),.?":{}|<>]/.test(password)) score += 1;
    else feedback.push('One special character');

    return {
      score,
      feedback,
      isValid: score === 5,
    };
  }
}

export const authApi = new AuthApiClient();
export { AuthApiClient };
